import { FileCreatedEvent } from '@rematter/frontend-lib-api-client/grpc/files/v1/file_events_pb';
import { Col, humanReadableDateTime } from '@rematter/ui-web';
import { FieldDisplay, IDField } from '@rematter/ui-web';

import { protoTimestampToZonedDateTimeString } from '@/audit-log/_components/_shared';
import { getLocale } from '~/shared/utils/locale';

type FileCreatedDetailProps = {
  event: FileCreatedEvent;
};

const formatFileSize = (size: bigint): string => {
  const bytes = Number(size);
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export const FileCreatedDetail = ({ event }: FileCreatedDetailProps) => {
  const file = event.file;

  if (!file) {
    return (
      <Col className="@FileCreatedDetail items-center justify-center p-4 text-neutral/40">
        <span>No data available</span>
      </Col>
    );
  }

  const createdAtStr = protoTimestampToZonedDateTimeString(file.createdAt);
  const expiresAtStr = protoTimestampToZonedDateTimeString(file.expiresAt);

  return (
    <Col className="@FileCreatedDetail gap-3" gap3>
      <Col className="rounded-md border border-neutral/10 bg-white p-3" gap2>
        <span className="text-xs font-medium text-neutral/60">
          File Details
        </span>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1">
          <IDField label="ID" value={file.id?.value} />
          <FieldDisplay label="File Name" value={file.fileName} />
          <FieldDisplay
            label="File Size"
            value={formatFileSize(file.fileSize)}
          />
          <FieldDisplay label="MIME Type" value={file.mimeType} />
          <FieldDisplay label="Status" value={file.status} />
          <FieldDisplay label="File Type" value={file.fileType} />
          <FieldDisplay label="Public" value={file.public ? 'Yes' : 'No'} />
          <FieldDisplay label="Entity Type" value={file.entityType} />
          <IDField label="Entity ID" value={file.entityId?.value} />
          <FieldDisplay label="Creator ID" value={event.creatorId} />
          <IDField label="Organization ID" value={file.organizationId?.value} />
          {file.parentId && (
            <FieldDisplay label="Parent ID" value={file.parentId} />
          )}
          {createdAtStr && (
            <FieldDisplay
              label="Created At"
              value={humanReadableDateTime(createdAtStr, {
                locale: getLocale(),
              })}
            />
          )}
          {expiresAtStr && (
            <FieldDisplay
              label="Expires At"
              value={humanReadableDateTime(expiresAtStr, {
                locale: getLocale(),
              })}
            />
          )}
        </div>
      </Col>
    </Col>
  );
};
